import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaUser, FaEnvelope, FaPhone, FaCheckCircle } from 'react-icons/fa';
import { createBooking, getApiErrorMessage } from '../../utils/api';
import services from '../../utils/services';

const BookingForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    time: '',
    address: '',
    termsAccepted: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState('');

  const timeSlots = [
    '7:00 AM', '7:30 AM', '8:00 AM', '8:30 AM',
    '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM',
    '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM',
    '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
    '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM',
    '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM',
  ];

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{8,15}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.service) {
      newErrors.service = 'Please select a service';
    }

    if (!formData.date) {
      newErrors.date = 'Please select a date';
    } else {
      const selectedDate = new Date(formData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.date = 'Date cannot be in the past';
      }
    }

    if (!formData.time) {
      newErrors.time = 'Please select a time';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.termsAccepted) {
      newErrors.termsAccepted = 'You must accept our Terms and Conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : name === 'phone' ? value.replace(/\D/g, '') : value,
    }));

    // Clear field error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    setServerError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    setServerError('');

    try {
      await createBooking(formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        date: '',
        time: '',
        address: '',
        termsAccepted: false,
      });
    } catch (error) {
      setServerError(getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // Get tomorrow's date as min date
  const getMinDate = () => {
    const today = new Date();
    today.setDate(today.getDate() + 1);
    return today.toISOString().split('T')[0];
  };

  if (success) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <FaCheckCircle className="text-4xl text-success" />
        </div>
        <h3 className="text-2xl font-heading font-bold text-gray-800 mb-3">
          Booking Confirmed!
        </h3>
        <p className="text-gray-500 mb-6 max-w-md mx-auto">
          Thank you for your booking. A confirmation email has been sent to your email address.
          Our team will contact you shortly to confirm the details.
        </p>
        <button
          onClick={() => setSuccess(false)}
          className="btn-primary"
        >
          Make Another Booking
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl shadow-lg p-6 md:p-10"
      noValidate
    >
      <h2 className="text-2xl font-heading font-bold text-gray-800 mb-2">
        Book a Cleaning Service
      </h2>
      <p className="text-gray-500 mb-8">
        Fill in the details below and we'll confirm your booking via email.
      </p>

      {serverError && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6 text-sm">
          {serverError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name */}
        <div>
          <label htmlFor="booking-name" className="form-label">
            <FaUser className="inline mr-2 text-primary-400" />
            Full Name
          </label>
          <input
            id="booking-name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="John Smith"
            className={`form-input ${errors.name ? 'border-red-400 focus:ring-red-400' : ''}`}
          />
          {errors.name && <p className="form-error">{errors.name}</p>}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="booking-email" className="form-label">
            <FaEnvelope className="inline mr-2 text-primary-400" />
            Email Address
          </label>
          <input
            id="booking-email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john@example.com"
            className={`form-input ${errors.email ? 'border-red-400 focus:ring-red-400' : ''}`}
          />
          {errors.email && <p className="form-error">{errors.email}</p>}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="booking-phone" className="form-label">
            <FaPhone className="inline mr-2 text-primary-400" />
            Phone Number
          </label>
          <input
            id="booking-phone"
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="0412345678"
            inputMode="numeric"
            autoComplete="tel"
            className={`form-input ${errors.phone ? 'border-red-400 focus:ring-red-400' : ''}`}
          />
          {errors.phone && <p className="form-error">{errors.phone}</p>}
        </div>

        {/* Service */}
        <div>
          <label htmlFor="booking-service" className="form-label">
            Select Service
          </label>
          <select
            id="booking-service"
            name="service"
            value={formData.service}
            onChange={handleChange}
            className={`form-input ${errors.service ? 'border-red-400 focus:ring-red-400' : ''}`}
          >
            <option value="">-- Choose a Service --</option>
            {services.map((s) => (
              <option key={s.id} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
          {errors.service && <p className="form-error">{errors.service}</p>}
        </div>

        {/* Date */}
        <div>
          <label htmlFor="booking-date" className="form-label">
            <FaCalendarAlt className="inline mr-2 text-primary-400" />
            Preferred Service Book Date
          </label>
          <input
            id="booking-date"
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            min={getMinDate()}
            className={`form-input ${errors.date ? 'border-red-400 focus:ring-red-400' : ''}`}
          />
          {errors.date && <p className="form-error">{errors.date}</p>}
        </div>

        {/* Time */}
        <div>
          <label htmlFor="booking-time" className="form-label">
            <FaClock className="inline mr-2 text-primary-400" />
            Preferred Time
          </label>
          <select
            id="booking-time"
            name="time"
            value={formData.time}
            onChange={handleChange}
            className={`form-input ${errors.time ? 'border-red-400 focus:ring-red-400' : ''}`}
          >
            <option value="">-- Choose a Time --</option>
            {timeSlots.map((slot) => (
              <option key={slot} value={slot}>
                {slot}
              </option>
            ))}
          </select>
          {errors.time && <p className="form-error">{errors.time}</p>}
        </div>

        {/* Address */}
        <div className="md:col-span-2">
          <label htmlFor="booking-address" className="form-label">
            <FaMapMarkerAlt className="inline mr-2 text-primary-400" />
            Service Address
          </label>
          <input
            id="booking-address"
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Brisbane QLD 4101"
            className={`form-input ${errors.address ? 'border-red-400 focus:ring-red-400' : ''}`}
          />
          {errors.address && <p className="form-error">{errors.address}</p>}
        </div>

        {/* Terms and Conditions */}
        <div className="md:col-span-2">
          <label htmlFor="booking-terms" className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
            <input
              id="booking-terms"
              type="checkbox"
              name="termsAccepted"
              checked={formData.termsAccepted}
              onChange={handleChange}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
            />
            <span>
              I accept the{' '}
              <Link to="/terms-of-service" className="text-primary-500 hover:text-primary-600 underline">
                Terms and Conditions
              </Link>
              .
            </span>
          </label>
          {errors.termsAccepted && <p className="form-error mt-1">{errors.termsAccepted}</p>}
        </div>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full mt-8 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            Processing...
          </>
        ) : (
          'Confirm Booking'
        )}
      </button>
    </form>
  );
};

export default BookingForm;